import useSoftLoadStore from '@state/SoftLoadStore';
import usePreviewMode from '@utils/hooks/usePreviewMode';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { PropsWithChildren, useRef } from 'react';

interface Props {
  readonly isAnimationDisabled?: boolean;
}

const SoftLoadWrapper = ({
  children,
  isAnimationDisabled,
}: PropsWithChildren<Props>) => {
  const isPreview = usePreviewMode();
  const shouldReduceMotion = useReducedMotion();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const isWrapperInView = useInView(wrapperRef, {
    once: true,
    margin: '100% 0px -15% 0px',
  });

  // isSoftLoadEnabled flag is initially undefined as we can't know on the server side
  // or on the initial render what its value will be, so we want to make sure the animation only
  // happen when we have the proper value set. In order to simulate the disabled soft load animation
  // but keep the same logic in place, we used transition duration 0 if flag is not true.
  // However, when flag is false, we still want to see the opacity animation
  // in order to have a smooth fade in, but we disable the y transition
  // ***********************************************************************************************
  // Animation can be enabled in Storybook by updating the flag in preview.ts file.
  const { isSoftLoadEnabled } = useSoftLoadStore((state) => ({
    isSoftLoadEnabled: state.isSoftLoadEnabled,
  }));

  if (isAnimationDisabled || isPreview) return <>{children}</>;

  return (
    <motion.div
      data-testid="Soft-Load-Wrapper"
      ref={wrapperRef}
      variants={{
        hidden: { opacity: 0, y: 60 },
        visible: { opacity: 1, y: 0 },
      }}
      initial="hidden"
      animate={
        (isSoftLoadEnabled && isWrapperInView) || isSoftLoadEnabled === false
          ? 'visible'
          : 'hidden'
      }
      transition={{
        opacity: {
          ease: 'easeOut',
          duration:
            shouldReduceMotion || isSoftLoadEnabled === undefined ? 0 : 0.6,
        },
        y: {
          ease: 'easeOut',
          duration: shouldReduceMotion || !isSoftLoadEnabled ? 0 : 0.6,
        },
      }}
    >
      {children}
    </motion.div>
  );
};

export default SoftLoadWrapper;
